import React, { useState, useEffect } from 'react';
import { apiGet, apiPost } from '../../api/apiClient';
import RTSpinner from '../common/RTSpinner';
import RTButton from '../common/RTButton';
import { toastSuccess, toastError } from '../common/toast';

const PGGeneralTab = ({ 
  pgData, 
  allocation, 
  isLoading, 
  allocationLoading, 
  isEditMode, 
  onEditToggle, 
  onRefresh 
}) => {
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  // Initialize edit data when edit mode is activated
  useEffect(() => {
    if (isEditMode && pgData) {
      const initialData = {
        name: pgData.name || '',
        dob: pgData.dob ? pgData.dob.split('T')[0] : '',
        gender: pgData.gender || '',
        aadhar: pgData.aadhar || '',
        student: pgData.student !== undefined ? pgData.student : false,
        working: pgData.working !== undefined ? pgData.working : false,
        food: pgData.food !== undefined ? pgData.food : false,
        mobile: pgData.mobile || '',
        whatsapp: pgData.whatsapp !== undefined ? pgData.whatsapp : false,
        address: pgData.address || '',
        email: pgData.email || '',
        parent_mobile: pgData.parent_mobile || ''
      };
      setEditData(initialData);
    }
  }, [isEditMode, pgData]);

  const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleInputChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };



  const handleSaveChanges = async () => {
    try {
      setSaving(true);
      
      // Prepare update data with proper null handling for optional fields
      const updateData = {
        ...editData,
        pg_id: pgData.pg_id,
        // Handle optional fields - send null if empty string
        aadhar: editData.aadhar && editData.aadhar.trim() !== '' ? editData.aadhar : null,
        email: editData.email && editData.email.trim() !== '' ? editData.email : null,
        parent_mobile: editData.parent_mobile && editData.parent_mobile.trim() !== '' ? editData.parent_mobile : null,
        address: editData.address && editData.address.trim() !== '' ? editData.address : null
      };
      
      await apiPost(`/pgdetails`, updateData);
      
      // Update the parent component's data
      await onRefresh();
      onEditToggle(); // Exit edit mode
      toastSuccess('PG details updated successfully!');
    } catch (error) {
      console.error('Error updating PG details:', error);
      toastError('Error updating PG details. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditData({});
    onEditToggle();
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <RTSpinner />
      </div>
    );
  }

  if (isEditMode) {
    return (
      <div className="rt-details-tabs-row" style={{ display: 'flex', gap: 32, marginTop: 18, flexDirection: 'column' }}>
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
          {/* Personal Details Section - Edit Mode */}
          <div className="rt-details-col">
            <h4 className="rt-details-section-title">Personal Details</h4>
            <div className="rt-details-form-table">
              <div className="rt-details-form-row">
                <div className="rt-details-form-label">Name</div>
                <div className="rt-details-form-value">
                  <input
                    type="text"
                    value={editData.name || ''}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="form-control"
                  />
                </div>
              </div>
              <div className="rt-details-form-row">
                <div className="rt-details-form-label">Date of Birth</div>
                <div className="rt-details-form-value">
                  <input
                    type="date"
                    value={editData.dob || ''}
                    onChange={(e) => handleInputChange('dob', e.target.value)}
                    className="form-control"
                  />
                </div>
              </div>
              <div className="rt-details-form-row">
                <div className="rt-details-form-label">Gender</div>
                <div className="rt-details-form-value">
                  <select
                    value={editData.gender || ''}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                    className="form-control"
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div className="rt-details-form-row">
                <div className="rt-details-form-label">Aadhar Number (Optional)</div>
                <div className="rt-details-form-value">
                  <input
                    type="text"
                    value={editData.aadhar || ''}
                    onChange={(e) => handleInputChange('aadhar', e.target.value)}
                    className="form-control"
                    placeholder="12 digit Aadhar number"
                    pattern="^\d{12}$"
                    maxLength="12"
                  />
                </div>
              </div>
              <div className="rt-details-form-row">
                <div className="rt-details-form-label">Student</div>
                <div className="rt-details-form-value">
                  <select
                    value={editData.student ? 'true' : 'false'}
                    onChange={(e) => handleInputChange('student', e.target.value === 'true')}
                    className="form-control"
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </div>
              </div>
              <div className="rt-details-form-row">
                <div className="rt-details-form-label">Working</div>
                <div className="rt-details-form-value">
                  <select
                    value={editData.working ? 'true' : 'false'}
                    onChange={(e) => handleInputChange('working', e.target.value === 'true')}
                    className="form-control"
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </div>
              </div>
              <div className="rt-details-form-row">
                <div className="rt-details-form-label">Food</div>
                <div className="rt-details-form-value">
                  <select
                    value={editData.food ? 'true' : 'false'}
                    onChange={(e) => handleInputChange('food', e.target.value === 'true')}
                    className="form-control"
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Communication Details Section - Edit Mode */}
          <div className="rt-details-col">
            <h4 className="rt-details-section-title">Communication Details</h4>
            <div className="rt-details-form-table">
              <div className="rt-details-form-row">
                <div className="rt-details-form-label">Mobile Number</div>
                <div className="rt-details-form-value">
                  <input
                    type="tel"
                    value={editData.mobile || ''}
                    onChange={(e) => handleInputChange('mobile', e.target.value)}
                    className="form-control"
                    placeholder="10 digit mobile number"
                    pattern="^\d{10}$"
                    maxLength="10"
                    required
                  />
                </div>
              </div>
              <div className="rt-details-form-row">
                <div className="rt-details-form-label">Whatsapp</div>
                <div className="rt-details-form-value">
                  <select
                    value={editData.whatsapp ? 'true' : 'false'}
                    onChange={(e) => handleInputChange('whatsapp', e.target.value === 'true')}
                    className="form-control"
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </div>
              </div>
              <div className="rt-details-form-row">
                <div className="rt-details-form-label">Address</div>
                <div className="rt-details-form-value">
                  <textarea
                    value={editData.address || ''}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    className="form-control"
                    rows="3"
                  />
                </div>
              </div>
              <div className="rt-details-form-row">
                <div className="rt-details-form-label">Email (Optional)</div>
                <div className="rt-details-form-value">
                  <input
                    type="email"
                    value={editData.email || ''}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="form-control"
                    placeholder="Enter valid email address"
                  />
                </div>
              </div>
              <div className="rt-details-form-row">
                <div className="rt-details-form-label">Parent/Guardian Mobile (Optional)</div>
                <div className="rt-details-form-value">
                  <input
                    type="tel"
                    value={editData.parent_mobile || ''}
                    onChange={(e) => handleInputChange('parent_mobile', e.target.value)}
                    className="form-control"
                    placeholder="10 digit mobile number"
                    pattern="^\d{10}$"
                    maxLength="10"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Save/Cancel Buttons - Centered below both sections */}
        <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
          <RTButton
            variant="primary"
            onClick={handleSaveChanges}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </RTButton>
          <RTButton
            variant="secondary"
            onClick={handleCancelEdit}
            disabled={saving}
          >
            Cancel
          </RTButton>
        </div>
      </div>
    );
  }

  return (
    <div className="rt-details-tabs-row" style={{ display: 'flex', gap: 32, marginTop: 18, flexWrap: 'wrap', minHeight: '500px' }}>
      {/* Personal Details Section */}
      <div className="rt-details-col">
        <h4 className="rt-details-section-title">Personal Details</h4>
        <div className="rt-details-form-table">
          <div className="rt-details-form-row">
            <div className="rt-details-form-label">Name</div>
            <div className="rt-details-form-value">{pgData.name}</div>
          </div>
          <div className="rt-details-form-row">
            <div className="rt-details-form-label">Date of Birth</div>
            <div className="rt-details-form-value">{formatDateToDDMMYYYY(pgData.dob)}</div>
          </div>
          <div className="rt-details-form-row">
            <div className="rt-details-form-label">Gender</div>
            <div className="rt-details-form-value">{pgData.gender}</div>
          </div>
          <div className="rt-details-form-row">
            <div className="rt-details-form-label">Aadhar Number</div>
            <div className="rt-details-form-value">{pgData.aadhar || '-'}</div>
          </div>
          <div className="rt-details-form-row">
            <div className="rt-details-form-label">Student</div>
            <div className="rt-details-form-value">{pgData.student ? 'Yes' : 'No'}</div>
          </div>
          <div className="rt-details-form-row">
            <div className="rt-details-form-label">Working</div>
            <div className="rt-details-form-value">{pgData.working ? 'Yes' : 'No'}</div>
          </div>
          <div className="rt-details-form-row">
            <div className="rt-details-form-label">Food</div>
            <div className="rt-details-form-value">{pgData.food ? 'Yes' : 'No'}</div>
          </div>
        </div>
      </div>

      {/* Communication Details Section */}
      <div className="rt-details-col">
        <h4 className="rt-details-section-title">Communication Details</h4>
        <div className="rt-details-form-table">
          <div className="rt-details-form-row">
            <div className="rt-details-form-label">Mobile Number</div>
            <div className="rt-details-form-value">{pgData.mobile}</div>
          </div>
          <div className="rt-details-form-row">
            <div className="rt-details-form-label">Whatsapp</div>
            <div className="rt-details-form-value">{pgData.whatsapp ? 'Yes' : 'No'}</div>
          </div>
          <div className="rt-details-form-row">
            <div className="rt-details-form-label">Address</div>
            <div className="rt-details-form-value">{pgData.address}</div>
          </div>
          <div className="rt-details-form-row">
            <div className="rt-details-form-label">Email</div>
            <div className="rt-details-form-value">{pgData.email || '-'}</div>
          </div>
          <div className="rt-details-form-row">
            <div className="rt-details-form-label">Parent/Guardian Mobile</div>
            <div className="rt-details-form-value">{pgData.parent_mobile || '-'}</div>
          </div>
          {pgData.status === 'Active' && allocation && (
            <div className="rt-details-form-row">
              <div className="rt-details-form-label">Current Building</div>
              <div className="rt-details-form-value">
                {allocationLoading ? (
                  <RTSpinner size="small" />
                ) : (
                  allocation.building_name || '-'
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PGGeneralTab;
